// Gegenstuecke zu den Pydantic-Schemas im Backend
// (backend/app/api/instanz_api.py, operator_api.py).

export interface ContainerInfo {
  name: string;
  service: string;
  status: string;
  health: string;
  image: string;
}

export interface InstanzUebersicht {
  slug: string;
  firmenname: string;
  admin_email: string | null;
  registriert: boolean;
  status: string;
  typ: 'KUNDE' | 'SYSTEM';
  umgebung: 'PROD' | 'TEST';
  image_tag: string | null;
  url: string | null;
  laeuft: boolean;
  container_anzahl: number;
  db_groesse_bytes: number | null;
  letztes_backup: string | null;
  verzeichnis: string | null;
}

export interface InstanzDetail extends InstanzUebersicht {
  container: ContainerInfo[];
  services: string[];
  notizen: string | null;
}

export interface DatenbankInfo {
  verfuegbar: boolean;
  hinweis: string | null;
  pfad: string | null;
  groesse_bytes: number | null;
  geaendert_am: string | null;
  alembic_version: string | null;
  tabellen_anzahl: number | null;
}

export interface InstanzMandant {
  tenant_id: number;
  slug: string | null;
  firmenname: string | null;
  status: string | null;
  branche: string | null;
  plan: string | null;
  max_benutzer: number | null;
}

export interface InstanzBenutzer {
  benutzer_id: number;
  name: string | null;
  email: string | null;
  rolle: string | null;
  aktiv: number | null;
  tenant_id: number | null;
  erstellt_am: string | null;
}

export interface BenutzerUebersicht {
  verfuegbar: boolean;
  hinweis: string | null;
  mandanten: InstanzMandant[];
  benutzer: InstanzBenutzer[];
}

export interface BackupInfo {
  id: number;
  dateiname: string;
  groesse_bytes: number;
  ausgeloest: string;
  erstellt_am: string;
}

export interface OperatorInfo {
  id: number;
  name: string;
  email: string;
  rolle: string;
  aktiv: boolean;
  gesperrt: boolean;
  erstellt_am: string | null;
}

/** Bytes menschenlesbar — Anzeige in Tabellen und Detailkarten. */
export function formatiereBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const einheiten = ['KB', 'MB', 'GB', 'TB'];
  let wert = bytes / 1024;
  let i = 0;
  while (wert >= 1024 && i < einheiten.length - 1) {
    wert /= 1024;
    i += 1;
  }
  return `${wert.toFixed(wert < 10 ? 1 : 0)} ${einheiten[i]}`;
}

/** ISO-Zeitstempel als deutsches Datum mit Uhrzeit. */
export function formatiereZeit(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export interface JobSchritt {
  instanz_slug: string;
  name: string;
  status: string;
  log: string;
  ts: string | null;
}

export interface Job {
  id: number;
  typ: string;
  status: string;
  image_tag: string | null;
  schritte: JobSchritt[];
  erstellt_am: string;
  beendet_am: string | null;
  freigabe_link?: string | null;
  freigabe_gesendet?: boolean | null;
}

export interface Ansprechpartner {
  id: number;
  instanz_id: number;
  name: string;
  email: string;
  typ: 'INTERN' | 'KUNDE';
  benachrichtigen: boolean;
  erstellt_am: string | null;
}

export interface FreigabeAnzeige {
  image_tag: string | null;
  ziel_slugs: string[];
  status: string;
  ablauf: string;
  bereits_bestaetigt: boolean;
}

export interface Plattenbelegung {
  pfad: string;
  gesamt_bytes: number | null;
  belegt_bytes: number | null;
  frei_bytes: number | null;
}

export interface SystemInfo {
  version: string;
  kunden: Plattenbelegung;
  backups: Plattenbelegung;
}

export interface AuditEintrag {
  id: number;
  zeitpunkt: string | null;
  benutzer_email: string | null;
  aktion: string;
  ziel_typ: string | null;
  ziel_id: string | null;
  details: string | null;
}

export interface SlugPruefung {
  frei: boolean;
  grund: string | null;
  host: string;
}

export interface SmtpStatus {
  quelle: 'db' | 'env' | 'keine';
  host: string | null;
  port: number | null;
  benutzer: string | null;
  absender: string | null;
  aktiv: boolean;
  passwort_gesetzt: boolean;
}
